// Staffing Requirement Calculator — shared defaults (kept in sync with backend staffing_pdf.py PDF_DEFAULTS)

export const ST_DEFAULT = {
  formulas: {
    sqft_per_guest: 18,
    cost_factor: 1.22,
    deployable_pct: 50,
    duty1_rooms: 15, duty1_seats: 60, duty1_capacity: 200,
    duty2_rooms: 40, duty2_capacity: 500,
    fo_base: 2, fo_rooms_per: 20, fo_event_threshold: 300,
    sec_per_gate: 4.2, sec_outdoor_threshold: 75000,
    hk_rooms_per: 12, hk_leave_factor: 1.25,
    hks_rooms: 15, hks_outdoor: 20000, hks2_rooms: 50, hks2_capacity: 700,
    pa_outdoor_per: 40000, pa_seats_per: 100, pa_leave_factor: 1.25,
    garden_per: 25000, garden_factor: 1.15,
    eng_min: 2, eng_rooms_per: 30, eng_outdoor_per: 75000,
    capt_per: 80, server_per: 20, server_leave_factor: 1.25,
    cook_per: 35, cook_min: 2, helper_per: 50, helper_min: 1, steward_per: 40, steward_min: 2,
    stores_rooms: 30, stores_seats: 100,
    laundry_rooms: 15,
  },
  roles: {
    property_manager: { label: "Property Manager", min: 50000, salary: 60000, max: 80000 },
    duty_manager: { label: "Duty Manager", min: 30000, salary: 35000, max: 45000 },
    front_office_exec: { label: "Front Office & Guest Relations", min: 20000, salary: 24000, max: 30000 },
    security_guard: { label: "Security Guard", min: 16000, salary: 18500, max: 22000 },
    room_attendant: { label: "Room Attendant", min: 15000, salary: 18000, max: 21000 },
    hk_supervisor: { label: "Housekeeping Supervisor", min: 22000, salary: 25000, max: 30000 },
    public_area_attendant: { label: "Public-Area Attendant", min: 14500, salary: 17000, max: 20000 },
    gardener: { label: "Gardener", min: 15000, salary: 17000, max: 20000 },
    technician: { label: "Multi-skilled Technician", min: 22000, salary: 26000, max: 32000 },
    fnb_captain: { label: "F&B Captain", min: 22000, salary: 26000, max: 32000 },
    server: { label: "Restaurant Server / Steward", min: 16000, salary: 18500, max: 22000 },
    chef_in_charge: { label: "Chef in Charge (Head Chef)", min: 60000, salary: 75000, max: 100000 },
    cook: { label: "Cook (Commis I)", min: 20000, salary: 23000, max: 28000 },
    kitchen_helper: { label: "Kitchen Helper", min: 14000, salary: 16500, max: 19000 },
    steward: { label: "Kitchen Steward", min: 15000, salary: 17000, max: 20000 },
    stores_admin: { label: "Storekeeper / Administration", min: 20000, salary: 24000, max: 30000 },
    laundry_coordinator: { label: "Linen Coordinator", min: 17000, salary: 20000, max: 24000 },
  },
  custom_roles: [],
  event_roles: [
    { id: "guest_relations", label: "Guest relations", per_guests: 100, min: 0, day_rate: 900 },
    { id: "event_security", label: "Event security", per_guests: 150, min: 0, day_rate: 800 },
    { id: "parking", label: "Parking team", per_guests: 200, min: 0, day_rate: 700 },
    { id: "event_housekeeping", label: "Event housekeeping", per_guests: 100, min: 0, day_rate: 700 },
    { id: "washroom", label: "Washroom attendants", per_guests: 250, min: 1, day_rate: 700 },
    { id: "event_servers", label: "Buffet service staff", per_guests: 25, min: 0, day_rate: 900 },
    { id: "event_cooks", label: "Event cooks", per_guests: 50, min: 0, day_rate: 1200 },
    { id: "event_helpers", label: "Event kitchen helpers", per_guests: 75, min: 0, day_rate: 800 },
    { id: "event_stewards", label: "Event stewarding", per_guests: 60, min: 0, day_rate: 700 },
    { id: "setup_runners", label: "Setup personnel & runners", per_guests: 50, min: 0, day_rate: 800 },
  ],
  complexity: {
    options: [
      { id: "compact", label: "Compact & operationally simple", desc: "Single block, easy access, low-maintenance land.", factor: 0.9, excluded_roles: [] },
      { id: "standard", label: "Standard Happy Retreats property", desc: "Typical celebration property layout.", factor: 1.0, excluded_roles: [] },
      { id: "spread", label: "Spread-out or operationally complex", desc: "Multiple villas, slopes or multiple guest zones.", factor: 1.1, excluded_roles: [] },
      { id: "premium", label: "Premium or highly complex", desc: "Premium positioning, highly landscaped, simultaneous events.", factor: 1.15, excluded_roles: [] },
    ],
  },
  health: {
    lean_below: 15, healthy_to: 19, review_to: 22,
    lean_label: "Lean — verify that service quality is protected",
    healthy_label: "Healthy operating range",
    review_label: "Review required",
    unsustainable_label: "Commercial model may be unsustainable",
  },
  hero: {
    overline: "Plan the team before the first guest arrives",
    headline_pre: "How many people does your ",
    headline_accent: "property",
    headline_post: " really need?",
    subheadline: "Our staffing formula separates the minimum permanent team that keeps your property guest-ready from the temporary crew added only on event days — so you are never paying wedding-scale salaries all year round.",
  },
  labels: {
    inputs_title: "Tell us about the property",
    inputs_intro: "Only four fundamentals drive the whole formula.",
    perm_title: "Minimum permanent team",
    perm_intro: "The team that keeps the property operational and guest-ready, every day of the year.",
    event_title: "Event-day temporary staffing",
    event_intro: "Added only when revenue-generating events are confirmed.",
    finance_title: "Financial impact",
    summary_overline: "Your staffing plan",
    download_label: "Download staffing plan (PDF)",
    discuss_label: "Discuss with our team",
    adjust_note: "This is the standard Happy Retreats starting point. Expect an adjustment of roughly 15–20% after the physical property assessment.",
    disclaimer: "This staffing plan is an illustrative estimate built from the inputs you entered and Happy Retreats' standard operating ratios. The final team structure is confirmed after a physical property assessment.",
  },
  closing: {
    enabled: true,
    heading: "The right team, at the right cost",
    text: "Our partnerships team will validate this staffing plan against your property's layout and service positioning — no obligations.",
    cta_label: "Partner with us",
  },
};

const mergeGroup = (d, c) => ({ ...d, ...(c || {}) });

export function mergeST(c) {
  const d = JSON.parse(JSON.stringify(ST_DEFAULT));
  if (!c) return d;
  const out = { ...d };
  out.formulas = mergeGroup(d.formulas, c.formulas);
  out.roles = { ...d.roles };
  for (const [k, v] of Object.entries(c.roles || {})) out.roles[k] = { ...(d.roles[k] || {}), ...v };
  out.event_roles = Array.isArray(c.event_roles) && c.event_roles.length ? c.event_roles : d.event_roles;
  out.custom_roles = Array.isArray(c.custom_roles) ? c.custom_roles : d.custom_roles;
  out.complexity = { ...d.complexity, ...(c.complexity || {}) };
  if (!(Array.isArray(out.complexity.options) && out.complexity.options.length)) out.complexity.options = d.complexity.options;
  out.health = mergeGroup(d.health, c.health);
  out.hero = mergeGroup(d.hero, c.hero);
  out.labels = mergeGroup(d.labels, c.labels);
  out.closing = mergeGroup(d.closing, c.closing);
  out.pdf = mergeGroup(d.pdf || {}, c.pdf);
  return out;
}
